import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Send, Image as ImageIcon, Trash2, Shield, Users, Trophy, Sparkles, X, Settings, Pencil } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function CommunityGroup() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    const [group, setGroup] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [imgUrl, setImgUrl] = useState("");
    const [showImage, setShowImage] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [myPosts, setMyPosts] = useState([]);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({ title: "", description: "", image_url: "" });
    const bottomRef = useRef(null);

    const loadGroup = async () => {
        const { data } = await api.get(`/community/groups/${id}`);
        setGroup(data);
        setEditForm({ title: data.title, description: data.description, image_url: data.image_url });
        return data;
    };
    const loadMessages = async (groupData) => {
        const g = groupData || group;
        // Only members or admin can read messages; non-members get an empty list
        if (!g) return;
        if (!g.is_member && !isAdmin) {
            setMessages([]);
            return;
        }
        try {
            const { data } = await api.get(`/community/groups/${id}/messages`);
            setMessages(data);
        } catch { /* 403 etc */ setMessages([]); }
    };
    const markSeen = () => api.post(`/community/groups/${id}/seen`).catch(() => {});

    useEffect(() => {
        (async () => {
            const g = await loadGroup();
            await loadMessages(g);
            markSeen();
        })();
        // poll for new messages every 4s — gated by membership in loadMessages
        const t = setInterval(loadMessages, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        markSeen();
        // eslint-disable-next-line
    }, [messages.length]);

    const join = async () => {
        try {
            await api.post(`/community/groups/${id}/join`);
            toast.success("Joined group");
            const g = await loadGroup();
            await loadMessages(g);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const leave = async () => {
        if (!confirm("Leave this group?")) return;
        await api.post(`/community/groups/${id}/leave`);
        toast.success("Left group");
        navigate("/community");
    };

    const sendText = async (e) => {
        e?.preventDefault();
        const t = text.trim();
        if (!t) return;
        try {
            await api.post(`/community/groups/${id}/messages`, { type: "text", content: t });
            setText("");
            loadMessages();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const sendImage = async (e) => {
        e?.preventDefault();
        const u = imgUrl.trim();
        if (!u) return;
        try {
            await api.post(`/community/groups/${id}/messages`, { type: "image", image_url: u, content: text.trim() });
            setImgUrl("");
            setText("");
            setShowImage(false);
            loadMessages();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const openShare = async () => {
        try {
            const { data } = await api.get("/finder/posts");
            const own = (data || []).filter(p => p.user_id === user?.id);
            setMyPosts(own);
            setShowShare(true);
            if (own.length === 0) toast.info("Post a game first to share it here");
        } catch (e) { toast.error("Failed to load your games"); }
    };

    const shareGame = async (postId) => {
        try {
            await api.post(`/finder/posts/${postId}/share`, { group_ids: [id] });
            toast.success("Shared to group");
            setShowShare(false);
            loadMessages();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const deleteMessage = async (msgId) => {
        if (!confirm("Delete this message?")) return;
        try {
            await api.delete(`/community/groups/${id}/messages/${msgId}`);
            loadMessages();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/community/groups/${id}`, editForm);
            toast.success("Group updated");
            setEditing(false);
            loadGroup();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const deleteGroup = async () => {
        if (!confirm("Delete this group permanently? All messages will be removed.")) return;
        try {
            await api.delete(`/community/groups/${id}`);
            toast.success("Group deleted");
            navigate("/community");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    if (!group) return <div className="max-w-3xl mx-auto p-6 text-slate-500">Loading...</div>;
    const canEdit = group.is_creator || isAdmin;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex flex-col h-[calc(100vh-72px)]">
            <Toaster theme="dark" richColors position="top-center" />
            <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-800 flex-shrink-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <button onClick={() => navigate("/community")} data-testid="back-community"
                        className="text-slate-400 hover:text-[#00FF66] flex-shrink-0">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    {group.image_url ? (
                        <img src={group.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-700 flex-shrink-0" />
                    ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-[#00FF66]" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="font-display font-bold uppercase text-white text-lg truncate" data-testid="group-title">{group.title}</div>
                        <div className="text-xs text-slate-500 truncate">{group.members_count} members{group.sport ? ` · ${group.sport}` : ""}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {canEdit && (
                        <button onClick={() => setEditing(true)} data-testid="edit-group-btn"
                            className="w-9 h-9 flex items-center justify-center rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300">
                            <Settings className="w-4 h-4" />
                        </button>
                    )}
                    {!group.is_member && !isAdmin && (
                        <button onClick={join} data-testid="join-group-btn"
                            className="px-3 h-9 bg-[#00FF66] text-black rounded text-xs font-bold uppercase tracking-widest hover:bg-[#00cc52]">
                            Join
                        </button>
                    )}
                    {group.is_member && !group.is_creator && !isAdmin && (
                        <button onClick={leave} data-testid="leave-group-btn"
                            className="px-3 h-9 border border-slate-700 text-slate-300 rounded text-xs font-bold uppercase tracking-widest hover:border-red-500/50 hover:text-red-400">
                            Leave
                        </button>
                    )}
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="messages-container">
                {!(group.is_member || isAdmin) ? (
                    <div className="text-center text-slate-500 text-sm py-12 border border-dashed border-slate-700 rounded-lg" data-testid="join-to-chat-msg">
                        🔒 Join the group to read & send messages.
                    </div>
                ) : messages.length === 0 ? (
                    <div className="text-center text-slate-500 text-sm py-8">No messages yet. Start the conversation 👋</div>
                ) : messages.map(m => (
                    <Message key={m.id} m={m} self={m.user_id === user?.id} canModerate={isAdmin || m.user_id === user?.id}
                        onDelete={() => deleteMessage(m.id)} />
                ))}
                <div ref={bottomRef} />
            </div>

            {/* Composer */}
            {(group.is_member || isAdmin) ? (
                <div className="flex-shrink-0 border-t border-slate-800 pt-3 space-y-2">
                    {showImage && (
                        <div className="flex gap-2 items-center">
                            <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)}
                                placeholder="Paste image URL (poster, screenshot, etc.)"
                                data-testid="composer-image-input"
                                className="flex-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            <button onClick={sendImage} disabled={!imgUrl} data-testid="send-image-btn"
                                className="px-3 h-10 bg-[#00FF66] text-black rounded text-xs font-bold uppercase tracking-widest disabled:opacity-40">Send Image</button>
                            <button onClick={() => { setShowImage(false); setImgUrl(""); }} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                    )}
                    <form onSubmit={sendText} className="flex gap-2 items-center">
                        <button type="button" onClick={() => setShowImage(v => !v)} title="Send image"
                            data-testid="toggle-image-btn"
                            className="w-10 h-10 flex items-center justify-center rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300">
                            <ImageIcon className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={openShare} title="Share my game"
                            data-testid="toggle-share-game-btn"
                            className="w-10 h-10 flex items-center justify-center rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300">
                            <Trophy className="w-4 h-4" />
                        </button>
                        <input value={text} onChange={(e) => setText(e.target.value)}
                            placeholder="Message…"
                            data-testid="composer-text-input"
                            className="flex-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        <button type="submit" disabled={!text.trim()}
                            data-testid="send-text-btn"
                            className="w-10 h-10 flex items-center justify-center rounded bg-[#00FF66] text-black hover:bg-[#00cc52] disabled:opacity-40">
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            ) : (
                <div className="flex-shrink-0 border-t border-slate-800 pt-4 text-center">
                    <p className="text-sm text-slate-400">Join the group to chat</p>
                </div>
            )}

            {/* Share game modal */}
            {showShare && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-5 max-h-[80vh] overflow-y-auto" data-testid="share-modal">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-xl font-bold uppercase text-white">Share a Game</h3>
                            <button onClick={() => setShowShare(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        {myPosts.length === 0 ? (
                            <div className="text-sm text-slate-400 text-center py-6">
                                You haven't posted any games. Go to <Link to="/community" className="text-[#00FF66] underline">Community</Link> and post one first.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {myPosts.map(p => (
                                    <button key={p.id} onClick={() => shareGame(p.id)} data-testid={`share-game-${p.id}`}
                                        className="w-full text-left bg-slate-950 border border-slate-700 rounded p-3 hover:border-[#00FF66]/50">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{p.sport}</span>
                                            <span className="text-white font-bold text-sm">{p.centre_name}</span>
                                        </div>
                                        <div className="text-xs text-slate-400 mt-1">{p.post_date} · {p.start_time} · {p.players_needed} players</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Edit / Settings modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <form onSubmit={saveEdit} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-3" data-testid="edit-group-form">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display text-xl font-bold uppercase text-white">Group Settings</h3>
                            <button type="button" onClick={() => setEditing(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Title</label>
                            <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} required
                                data-testid="edit-group-title"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Description</label>
                            <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2}
                                className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Cover Image URL</label>
                            <input value={editForm.image_url} onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                        </div>
                        <div className="flex flex-wrap gap-2 pt-2">
                            <button type="submit" data-testid="save-group-btn" className="flex-1 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">Save</button>
                            {(group.is_creator || isAdmin) && (
                                <button type="button" onClick={deleteGroup} data-testid="delete-group-btn"
                                    className="px-3 h-10 border border-red-900/50 text-red-400 rounded text-xs font-bold uppercase tracking-widest hover:bg-red-950">
                                    <Trash2 className="w-3 h-3 inline-block mr-1" /> Delete
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Message({ m, self, canModerate, onDelete }) {
    const isAdmin = m.user_role === "admin" || m.user_role === "super_admin";
    const time = new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return (
        <div className={`flex ${self ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
            <div className={`max-w-[80%] group relative ${self ? "ml-12" : "mr-12"}`}>
                <div className={`flex items-center gap-2 mb-1 ${self ? "justify-end" : ""}`}>
                    <span className="font-bold text-xs text-slate-200">{self ? "You" : m.user_name}</span>
                    {isAdmin && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/40">
                            <Shield className="w-2.5 h-2.5" /> Admin
                        </span>
                    )}
                    <span className="text-[10px] text-slate-500">{time}</span>
                </div>
                <div className={`rounded-2xl px-4 py-2 ${
                    self ? "bg-[#00FF66]/15 border border-[#00FF66]/40 text-white" : "bg-slate-900 border border-slate-800 text-slate-100"
                }`}>
                    {m.type === "text" && <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>}
                    {m.type === "image" && (
                        <>
                            <img src={m.image_url} alt="shared" className="rounded max-w-full max-h-72 object-contain" />
                            {m.content && <p className="text-sm mt-2 whitespace-pre-wrap break-words">{m.content}</p>}
                        </>
                    )}
                    {m.type === "game_post" && m.game_post_snapshot && (
                        <div className="space-y-1 min-w-[220px]">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-3.5 h-3.5 text-[#00FF66]" />
                                <span className="text-[10px] uppercase tracking-widest font-bold text-[#00FF66]">Game posted</span>
                            </div>
                            <div className="font-bold uppercase text-white text-sm">{m.game_post_snapshot.sport} · {m.game_post_snapshot.centre_name}</div>
                            <div className="text-xs text-slate-300">{m.game_post_snapshot.post_date} · {m.game_post_snapshot.start_time}</div>
                            <div className="text-xs text-slate-400">{m.game_post_snapshot.players_needed} players needed · {m.game_post_snapshot.joiners_count} joined</div>
                            {m.game_post_snapshot.note && <div className="text-xs text-slate-400 mt-1 italic">"{m.game_post_snapshot.note}"</div>}
                            <Link to="/community" className="inline-block mt-2 text-[10px] uppercase tracking-widest font-bold text-[#00FF66] hover:underline">Open in Community →</Link>
                        </div>
                    )}
                </div>
                {canModerate && (
                    <button onClick={onDelete} data-testid={`delete-msg-${m.id}`}
                        className="absolute top-0 -right-7 opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition">
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
